import React, { useCallback, useEffect, useMemo, useState } from "react";

type FullscreenControllerProps = {
  selector: string;
  retryFindMs?: number;

  // Class name applied to the target element while fullscreen is active
  enterClassName?: string;

  // Optional side-effects when entering/exiting fullscreen
  onEnter?: (el: Element) => void;
  onExit?: (el: Element) => void;

  children: (args: {
    isSupported: boolean;
    isFullscreen: boolean;
    enter: () => Promise<void>;
    exit: () => Promise<void>;
    toggle: () => Promise<void>;
  }) => React.ReactNode | any;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;

  webkitExitFullscreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
};

type FullscreenElement = Element & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

function getFullscreenElement(doc: FullscreenDocument): Element | null {
  return (
    doc.fullscreenElement ??
    doc.webkitFullscreenElement ??
    doc.mozFullScreenElement ??
    doc.msFullscreenElement ??
    null
  );
}

function isFullscreenSupported(doc: FullscreenDocument): boolean {
  const el = doc.documentElement as FullscreenElement;

  return Boolean(
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen ||
    doc.exitFullscreen ||
    doc.webkitExitFullscreen ||
    doc.mozCancelFullScreen ||
    doc.msExitFullscreen
  );
}

async function requestFullscreen(target: FullscreenElement): Promise<void> {
  if (target.requestFullscreen) return target.requestFullscreen();
  if (target.webkitRequestFullscreen) return target.webkitRequestFullscreen();
  if (target.mozRequestFullScreen) return target.mozRequestFullScreen();
  if (target.msRequestFullscreen) return target.msRequestFullscreen();
  throw new Error("Fullscreen API is not available on this element");
}

async function exitFullscreen(doc: FullscreenDocument): Promise<void> {
  if (doc.exitFullscreen) return doc.exitFullscreen();
  if (doc.webkitExitFullscreen) return doc.webkitExitFullscreen();
  if (doc.mozCancelFullScreen) return doc.mozCancelFullScreen();
  if (doc.msExitFullscreen) return doc.msExitFullscreen();
  throw new Error("Fullscreen exit is not available");
}

export function FullscreenController({
                                       selector,
                                       retryFindMs = 0,
                                       enterClassName,
                                       onEnter,
                                       onExit,
                                       children,
                                     }: FullscreenControllerProps) {
  const doc = document as FullscreenDocument;

  const [target, setTarget] = useState<FullscreenElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Compute support once per document reference
  const isSupported = useMemo(() => isFullscreenSupported(doc), [doc]);

  useEffect(() => {
    let timer: number | undefined;

    // Find the target element by selector (optionally retry if it appears later)
    const find = () => {
      const el = document.querySelector(selector) as FullscreenElement | null;
      if (el) {
        setTarget(el);
        return;
      }
      setTarget(null);

      if (retryFindMs > 0) {
        timer = window.setTimeout(find, retryFindMs);
      }
    };

    find();

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [selector, retryFindMs]);

  useEffect(() => {
    if (!isSupported) return;

    // Keep isFullscreen in sync and apply/remove the styling class
    const sync = () => {
      const fsActive = Boolean(getFullscreenElement(doc));
      setIsFullscreen(fsActive);

      if (!target || !enterClassName) return;

      if (fsActive) {
        target.classList.add(enterClassName);
        onEnter?.(target);
      } else {
        target.classList.remove(enterClassName);
        onExit?.(target);
      }
    };

    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync as EventListener);
    document.addEventListener("mozfullscreenchange", sync as EventListener);
    document.addEventListener("MSFullscreenChange", sync as EventListener);

    sync();

    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync as EventListener);
      document.removeEventListener("mozfullscreenchange", sync as EventListener);
      document.removeEventListener("MSFullscreenChange", sync as EventListener);

      // Ensure class cleanup if the component unmounts while fullscreen is active
      if (target && enterClassName) {
        target.classList.remove(enterClassName);
      }
    };
  }, [doc, isSupported, target, enterClassName, onEnter, onExit]);

  const enter = useCallback(async () => {
    if (!isSupported || !target) return;

    // Some browsers require a user gesture; ignore errors silently
    try {
      await requestFullscreen(target);
    } catch {
      // noop
    }
  }, [isSupported, target]);

  const exit = useCallback(async () => {
    if (!isSupported) return;
    if (!getFullscreenElement(doc)) return;

    try {
      await exitFullscreen(doc);
    } catch {
      // noop
    }
  }, [doc, isSupported]);

  const toggle = useCallback(async () => {
    if (!isSupported) return;

    const fsEl = getFullscreenElement(doc);
    if (fsEl) await exit();
    else await enter();
  }, [doc, enter, exit, isSupported]);

  /* @ts-ignore */
  return children({ isSupported, isFullscreen, enter, exit, toggle });
}
